import { isPrivateEmail } from '@zaplify/utils';
import { LinkedinProfileDto, LinkedinProfileExperienceDto } from '@andsend/services/linkedin-profiles/shared';
import { CompanyParsed } from './interfaces';
import { ProfileFullWithExperienceParsed } from './interfaces/profile-full.interface';
import {
    LinkedinVoyagerConversationMessage,
    ConversationMessageParsed,
    LinkedinVoyagerRenderContent,
    ConversationMessageAttachmentParsed,
} from './interfaces/conversation.interface';

export function parseConversationMessage(
    message: LinkedinVoyagerConversationMessage,
    userMemberId: string,
): ConversationMessageParsed | undefined {
    if (message?._type !== 'com.linkedin.messenger.Message') return undefined;

    const senderURN = message.sender.entityUrn;
    const senderId = senderURN.split('urn:li:fsd_profile:')[1];

    const participantUserIds =
        message.conversation.conversationParticipants?.map(
            (participant) => participant.entityUrn.split('urn:li:fsd_profile:')[1],
        ) || [];
    const receivers = participantUserIds.filter((participantId) => participantId !== senderId);

    const messageText = message.body.text;
    const messageSubject = message.subject ? message.subject : undefined;
    const messageId = message.backendUrn.split('urn:li:messagingMessage:')[1];
    const conversationId = message.backendConversationUrn.split('urn:li:messagingThread:')[1];
    const timestamp = message.deliveredAt;

    const direction = senderId === userMemberId ? 'OUT' : 'IN';

    return {
        direction,
        timestamp: timestamp,
        data: {
            senderId: senderId,
            receiverIds: receivers,
            entityUrn: message.entityUrn,
            subject: messageSubject,
            message: messageText,
            messageId: messageId,
            conversationId: conversationId,
            attachments: parseMessageAttachments(message.renderContent),
        },
    };
}

function parseMessageAttachments(
    renderContent: LinkedinVoyagerRenderContent[],
): ConversationMessageAttachmentParsed | undefined {
    try {
        const imageUrls = renderContent?.map((content) => content?.vectorImage?.rootUrl)?.filter((x) => x);

        const feedUpdateHostUrns = renderContent
            ?.filter((content) => content?.hostUrnData?.type === 'FEED_UPDATE')
            ?.map((content) => content?.hostUrnData?.hostUrn);

        // Has the structure "urn:li:fsd_update:(urn:li:activity:666666,MESSAGING_RESHARE,EMPTY,DEFAULT,false)"
        // These can be mapped to linkedin.com/feed/update/urn:li:activity:66666/
        const feedUpdateUrls = feedUpdateHostUrns
            ?.map((hostUrn) => {
                try {
                    const urlEnd = hostUrn?.match(/urn:li:activity:\d+/)?.[0];
                    if (!urlEnd) return undefined;
                    return `https://linkedin.com/feed/update/${urlEnd}`;
                } catch (error) {
                    console.error('Error parsing feed update host urn:', error);
                    return undefined;
                }
            })
            .filter((x) => x) as string[];

        return {
            imageUrls,
            feedUpdates: feedUpdateUrls,
        };
    } catch (error) {
        console.error('Error parsing message attachments:', error);
        return undefined;
    }
}

export const webExtLiProfileToLinkedinProfileDto = (
    extensionProfile: ProfileFullWithExperienceParsed,
): LinkedinProfileDto => {
    let topExperience = extensionProfile.experiences?.[0];

    if (extensionProfile.topPosition) {
        const topPositionExperience = extensionProfile.experiences.find(
            (experience) =>
                experience.companyName === extensionProfile.topPosition.companyName &&
                experience.startDate == extensionProfile.topPosition.startDate &&
                experience.endDate == extensionProfile.topPosition.endDate,
        );
        if (topPositionExperience) {
            topExperience = topPositionExperience;
        }
    }

    const email = isPrivateEmail(extensionProfile.contactInfo?.emailAddress)
        ? undefined
        : extensionProfile.contactInfo?.emailAddress;

    return new LinkedinProfileDto({
        memberId: extensionProfile.memberId,
        memberNumberId: undefined,
        publicIdentifier: extensionProfile.publicIdentifier,
        firstName: extensionProfile.firstName,
        lastName: extensionProfile.lastName,
        fullName: `${extensionProfile.firstName} ${extensionProfile.lastName || ''}`,
        profileImageUrl: extensionProfile.profilePicture,
        numberOfConnections: extensionProfile.numberOfConnections,
        headline: extensionProfile.headline,
        personalSummary: extensionProfile.aboutSummary,
        locationString: extensionProfile.location,
        email: email,
        phoneNumbers: extensionProfile.contactInfo?.phoneNumbers,
        experiences: extensionProfile.experiences?.map(
            (experience) =>
                new LinkedinProfileExperienceDto({
                    isCurrent: experience === topExperience,
                    summary: experience.description,
                    companyName: experience.companyName,
                    jobTitle: experience.title,
                    jobStartDate: liProfileDateParser(experience.startDate)?.toDateString(),
                    jobEndDate: experience.endDate
                        ? liProfileDateParser(experience.endDate)?.toDateString()
                        : undefined,
                    // Need to be "enriched"
                    companyId: experience.companyId,
                    companyPublicIdentifier: experience.companyPublicIdentifier,
                    companyWebsite: undefined, // experience.company?.websiteUrl,
                    companyIndustry: undefined, // experience.company?.industries?.[0],
                    companySizeRange: undefined, // experience.company?.size,
                    companyImageUrl: undefined, // experience.imageUrl,
                    companyDescription: undefined, // experience.company?.description,
                }),
        ),
        lastChecked: new Date(),
    });
};

const liProfileDateParser = (date: { month?: number; year: number }) => {
    if (!date) return null;
    return new Date(date.year, date.month ? date.month - 1 : 0);
};

const liProfileParseCompany = (company: CompanyParsed) => {
    return {
        id: company.universalName,
        companyName: company.name,
        companyWebsite: company.websiteUrl,
        companyIndustry: company.industries?.[0],
        companyId: company.universalName,
        companyPublicIdentifier: company.universalName,
        companySizeRange: company?.size,
        companyImageUrl: company.imageUrl,
        companyDescription: company.description,
    };
};
